/**
 * Path-based product persona: consumer (user) vs travel professional (pro).
 * Stored in localStorage; not a Cognito account attribute.
 */

export type Persona = 'user' | 'pro';

const PERSONA_KEY = 'wt_persona';

export function getPersona(): Persona {
  try {
    const raw = localStorage.getItem(PERSONA_KEY);
    if (raw === 'pro' || raw === 'user') return raw;
  } catch {
    /* ignore */
  }
  return 'user';
}

export function setPersona(persona: Persona): void {
  try {
    localStorage.setItem(PERSONA_KEY, persona);
  } catch {
    /* ignore */
  }
}

export function defaultHomePath(persona: Persona = getPersona()): string {
  return persona === 'pro' ? '/clients' : '/trips';
}

/** True if path belongs to the travel-professional product surface. */
export function isProPath(pathname: string): boolean {
  return (
    pathname === '/list-builder' ||
    pathname.startsWith('/list-builder/') ||
    pathname === '/clients' ||
    pathname.startsWith('/clients/')
  );
}

/** True if path belongs to the consumer product surface. */
export function isUserPath(pathname: string): boolean {
  return pathname === '/trips' || pathname.startsWith('/trips/');
}

/**
 * Resolve post-login redirect: honor stored path when it matches persona,
 * otherwise fall back to the persona home.
 */
export function resolvePostLoginRedirect(storedPath: string | null): string {
  const persona = getPersona();
  if (!storedPath) return defaultHomePath(persona);

  if (persona === 'user' && isProPath(storedPath)) return defaultHomePath('user');
  if (persona === 'pro' && isUserPath(storedPath)) return defaultHomePath('pro');
  return storedPath;
}
